import 'prosemirror-view/style/prosemirror.css';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import '@benrbray/prosemirror-math/style/math.css';
import 'katex/dist/katex.min.css';

import React, { Suspense, useMemo } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { PRISM_PLUGIN_KEY } from '@lyearn/lygo-blocks';
import { AddCoverDialogContext, Editor as EditorCore, UrlPreviewContext } from '@lyearn/lygo-core';
import ImportLoader from '@/components/Loader';
import { EditorProps } from '@/components/PageEditor/types';
import { get, post } from '@/helper/request';
import { useAppConfig } from '@/hooks/useAppConfig';
import useConvertFile from '@/hooks/useConvertFile';
import { useUrlPreviewsQuery } from '@/hooks/useUrlPreview/queries/generated/urlPreview';
import AddCoverDialog from '@/modules/corporate/components/AddCoverDialog';
import VideoDownloadProvider from '@/modules/corporate/modules/course/context/VideoDownloadProvider';
import { MediaKindEnum } from '@/types/schema';

import { ProseMirrorNodeProvider } from '../../modules/corporate/modules/course/context/ProseMirrorNodeProvider';
import Loader from './Loader';
import useAddCoverDialog from './useAddCoverDialog';

const contextValue = { useUrlPreviewsQuery };

const enabledPluginsInReadOnly = [PRISM_PLUGIN_KEY];

const Editor: React.FC<EditorProps> = ({
  loading,
  jsonDoc,
  yProvider,
  schema,
  importing,
  importingLabel,
  LoaderComponent = <Loader />,
  ...props
}) => {
  const doc = useMemo(
    () =>
      !loading && jsonDoc
        ? schema.nodeFromJSON({ type: 'doc', content: jsonDoc.content })
        : undefined,
    [jsonDoc, loading, schema],
  );

  const { open, cover, onCloseDialog, onSubmit, openDialog } = useAddCoverDialog();
  const { site } = useAppConfig();
  const { enabledConvertibleImport } = site;
  const { t } = useTranslation();

  if (importing) {
    return <ImportLoader defaultLabel={importingLabel} />;
  }

  if ((!doc && !yProvider) || loading) {
    return LoaderComponent;
  }

  return (
    <Suspense fallback={null}>
      <VideoDownloadProvider>
        <AddCoverDialogContext.Provider value={{ openDialog }}>
          <UrlPreviewContext.Provider value={contextValue}>
            <ProseMirrorNodeProvider node={doc}>
              <EditorCore
                {...props}
                doc={doc}
                enabledConvertibleImport={enabledConvertibleImport}
                enabledPluginsInReadOnly={enabledPluginsInReadOnly}
                getRequest={get}
                postRequest={post}
                schema={schema}
                useConvertFile={useConvertFile}
                yProvider={yProvider!}
              />
            </ProseMirrorNodeProvider>
          </UrlPreviewContext.Provider>
        </AddCoverDialogContext.Provider>
      </VideoDownloadProvider>
      {open ? (
        <AddCoverDialog
          cover={{ ...cover, kind: MediaKindEnum.Image }}
          disableRestoreFocus={true}
          i18n={{ headerTitle: t('Common:Image.AddAnImage') }}
          onClose={onCloseDialog}
          onSubmit={onSubmit}
        />
      ) : null}
    </Suspense>
  );
};

export default React.memo(Editor);
